/**
 * The game, attached to a hosted world.
 *
 * `src/host/client.ts` owns the wire and the clock. This owns what the *game*
 * does with them: when to adopt a world, when the local copy has gone stale,
 * what happens to a cast, and where the other gods are. `main.ts` holds one of
 * these or none, and everything about being attached hangs off it.
 *
 * ## The client still runs the simulation — and that is the whole design
 *
 * A naive attached client would freeze its own world and refetch constantly:
 * three and a half megabytes every second, and bands that teleport. This one
 * instead **predicts**, and the reason it can is the property this repository
 * has spent a lot of proof on:
 *
 *     the same snapshot + the same absolute grid + the same world time
 *       = the same world, bit for bit
 *
 * So an attached client adopts the server's snapshot and then advances its own
 * `Simulation` exactly as an unattached one does — with one change: `clock.t`
 * comes from the server's canonical clock instead of from local `dt`. Both
 * sides run the identical code over the identical grid from the identical
 * state, so they stay in lockstep for free. `test/persistence-equivalence.ts`
 * is what makes that a fact rather than a hope.
 *
 * ## …until somebody acts, and then it says so
 *
 * A prediction is only valid while nothing enters the world from outside. The
 * instant any god's intent is accepted — theirs or ours — the server's world
 * diverges from every client's, and there is exactly one honest response:
 * refetch. The server tells us precisely when, and it costs nothing to notice,
 * because **`intentSeq` rides on every message**. A bump means the world moved
 * in a way we could not have predicted; no bump means our copy is still right.
 *
 * That makes the resync rate equal to the rate at which people press keys —
 * a few per minute for a handful of friends — rather than a fixed poll. What
 * it is NOT is cheap at scale: every accepted act costs every connected client
 * a full snapshot fetch. On a LAN with four friends that is fine and measured;
 * it is the first thing to replace with a delta if this ever leaves the LAN,
 * and `SERVER.md` records it as such.
 *
 * ## What this module refuses to do
 *
 * It never mutates a `Simulation`. It hands `main.ts` a snapshot to adopt and
 * a result to draw, and `main.ts` — which is the only writer — does both. That
 * keeps the one-writer rule intact under attachment, which matters more here
 * than anywhere: a second writer on an attached client is a fork nobody would
 * see until the worlds had visibly parted.
 */

import type { SimulationSnapshot } from '@/sim/persistence/snapshot';
import type { DivineFocus, DivineResult, GraspResult } from '@/sim/simulation';
import type { EventRecord } from '@/sim/events';
import { connectToWorld, type WorldConnection } from '@/host/client';
import type { AckMessage, IntentResult } from '@/host/protocol';

/** What `?server=` asked for. */
export interface AttachRequest {
  origin: string;
  token: string;
  /** A session id from a previous visit, so a reload keeps the same god. */
  session?: string;
  /** What to call this player to the others. Never reaches the world. */
  name?: string;
}

/**
 * Read the attachment out of a URL. Absent means single-player, which is the
 * default and stays the default: attaching is something a URL asks for.
 *
 *     ?server=http://192.168.1.8:8787&token=our-secret
 *     ?server=8787                        …shorthand for this host, that port
 */
export function attachRequestFrom(search: string): AttachRequest | null {
  const q = new URLSearchParams(search);
  const raw = q.get('server');
  if (!raw) return null;
  const token = q.get('token') ?? '';
  // A bare port is the common case when a friend is on the same machine or the
  // link was typed by hand; anything else is taken as an origin.
  const origin = /^\d+$/.test(raw)
    ? `${location.protocol}//${location.hostname}:${raw}`
    : /^https?:\/\//.test(raw) ? raw : `http://${raw}`;
  const session = q.get('session') ?? sessionFor(origin) ?? undefined;
  const name = q.get('name') ?? undefined;
  return { origin, token, ...(session ? { session } : {}), ...(name ? { name } : {}) };
}

/**
 * The session id this TAB last held for this server, so a reload comes back as
 * the same god rather than as a stranger.
 *
 * **`sessionStorage`, and the difference from `localStorage` is a bug this
 * had.** `localStorage` is shared by every tab on an origin, so opening a
 * second window on the same server overwrote the first one's session — and
 * then the first tab's next reload presented the second tab's id and was
 * handed the second tab's GOD. Two players, one deity, and the acts of one
 * journaled under the other. `sessionStorage` is per tab by definition and
 * survives that tab's reloads, which is exactly the lifetime "which god is
 * playing in this window" actually has.
 *
 * What it costs: closing the tab forgets the god. That is the honest limit of
 * a browser-storage credential, and the fix for it is a real one — an identity
 * the player carries rather than one the browser happens to remember. `?session=`
 * is the manual override in the meantime, and it beats storage on read.
 */
const SESSION_KEY = (origin: string) => `unseen-session:${origin}`;

const sessionFor = (origin: string): string | null => {
  try { return sessionStorage.getItem(SESSION_KEY(origin)); } catch { return null; }
};

const rememberSession = (origin: string, session: string): void => {
  try { sessionStorage.setItem(SESSION_KEY(origin), session); } catch { /* private mode */ }
};

export interface AttachHooks {
  /** Events the world emitted. Includes our own, and every other god's. */
  onEvents?: (events: EventRecord[]) => void;
  /** Something worth telling the player, in the game's own voice. */
  onNotice?: (text: string) => void;
  /** The connection went away. The world is now un-attached and frozen. */
  onLost?: (why: string) => void;
}

export interface AttachedWorld {
  readonly conn: WorldConnection;
  /** Whose hands this browser has on this world. Not `player`. */
  readonly deityId: string;
  /** What this player is called to the others, if anything. */
  readonly name: string | undefined;
  readonly worldId: string;

  /** Canonical world time now, extrapolated from the last heartbeat. */
  worldTimeNow(): number;

  /**
   * True when the local copy is known to have diverged — somebody's intent was
   * accepted, or the server said resync. Cleared by `takeWorld`.
   */
  readonly stale: boolean;
  /**
   * The world, if it is worth refetching right now: stale, and not fetched too
   * recently. Null means "keep predicting". Throttled because a burst of acts
   * would otherwise queue a burst of multi-megabyte fetches.
   */
  takeWorld(): Promise<SimulationSnapshot | null>;

  /** Where every connected god is looking, and what it calls itself. */
  readonly others: ReadonlyArray<
    { deityId: string; x: number; y: number; z: number; name?: string }>;

  /** Tell the server where this god is looking. Writes nothing, anywhere. */
  look(p: { x: number; y: number; z: number; ground: number }): void;

  /** Ask the world to act. Resolves with the world's own answer, or null. */
  act(req: {
    verb: 'strike' | 'rain' | 'strengthen' | 'weaken' | 'omen';
    x: number; z: number; focus: DivineFocus;
    from?: { x: number; z: number };
    spend?: number;
    target?: { kind: string; id: string; aimed?: boolean;
               herd?: { species: string; sx: number; sz: number };
               plant?: { kind: string } };
  }): Promise<DivineResult | null>;

  grasp(req: { fromX: number; fromZ: number; toX: number; toZ: number }):
    Promise<GraspResult | null>;

  anchor(req: { x: number; z: number }):
    Promise<{ ok: boolean; reason?: string } | null>;

  close(): void;
}

/** Least real time between two world fetches. A burst of acts costs one. */
const RESYNC_THROTTLE_MS = 400;

/**
 * Connect, and hand back the world to adopt along with the handle.
 *
 * Rejects rather than degrading: a client that could not reach the server, or
 * that speaks a different protocol, must say so and fall back to the local
 * world rather than half-attaching. `main.ts` catches and carries on offline.
 */
export async function attach(
  req: AttachRequest, hooks: AttachHooks = {},
): Promise<{ world: AttachedWorld; snapshot: SimulationSnapshot }> {
  let stale = false;
  let lastSeq = -1;
  let lastFetch = -1e9;
  let inFlight: Promise<SimulationSnapshot> | null = null;

  /**
   * Any accepted intent means the world moved in a way this client could not
   * have predicted. Ours included: our own cast was applied on the server, not
   * here. One counter, one rule, no special cases.
   */
  const noteSeq = (seq: number) => {
    if (lastSeq >= 0 && seq > lastSeq) stale = true;
    lastSeq = Math.max(lastSeq, seq);
  };

  const conn = await connectToWorld({
    origin: req.origin,
    token: req.token,
    ...(req.session ? { session: req.session } : {}),
    ...(req.name ? { name: req.name } : {}),
    onTick: (m) => noteSeq(m.intentSeq),
    onEvents: (m) => { noteSeq(m.intentSeq); hooks.onEvents?.(m.events); },
    onAck: (m) => noteSeq(m.intentSeq),
    onSnapshotReady: (m) => { noteSeq(m.intentSeq); stale = true; },
    onError: (m) => hooks.onNotice?.(m.reason),
    onClosing: (m) => hooks.onNotice?.(`the world is closing — ${m.reason}`),
    onClosed: (info) => {
      if (!info.deliberate) hooks.onLost?.(info.reason || `connection lost (${info.code})`);
    },
    onProtocolError: (why) => hooks.onNotice?.(`the server said something odd: ${why}`),
  });
  rememberSession(req.origin, conn.session);
  lastSeq = conn.welcome.intentSeq;
  let snapshot: SimulationSnapshot;
  try {
    snapshot = await conn.fetchWorld();
  } catch (e) {
    // The socket opened and the world did not arrive — a CORS block, a version
    // mismatch, a half-deployed server. Close it rather than leaving a live
    // connection behind a failed attach: the caller is about to fall back to
    // its own world, and a socket still taking ticks for a world nobody is
    // looking at is the kind of thing that is only noticed weeks later.
    conn.close();
    throw e;
  }
  lastFetch = performance.now();

  /** Pull the right half out of a typed result, or null if it is another kind. */
  const resultOf = <K extends IntentResult['kind']>(
    ack: AckMessage, kind: K,
  ): Extract<IntentResult, { kind: K }> | null => {
    const r = ack.result;
    return r && r.kind === kind ? (r as Extract<IntentResult, { kind: K }>) : null;
  };

  const refused = (ack: AckMessage): void => {
    if (!ack.accepted && ack.reason) hooks.onNotice?.(ack.reason);
  };

  const world: AttachedWorld = {
    conn,
    deityId: conn.deityId,
    get name() { return conn.name; },
    worldId: conn.worldId,
    worldTimeNow: () => conn.worldTimeAt(Date.now()),
    get stale() { return stale; },
    get others() {
      return conn.presences.filter((p) => p.deityId !== conn.deityId);
    },

    async takeWorld(): Promise<SimulationSnapshot | null> {
      if (!stale || !conn.connected) return null;
      if (performance.now() - lastFetch < RESYNC_THROTTLE_MS) return null;
      // One fetch at a time. Two acts a frame apart would otherwise start two
      // multi-megabyte requests and adopt them in whatever order they landed —
      // and adopting an OLDER world after a newer one is a visible rewind.
      if (inFlight) return null;
      stale = false;
      lastFetch = performance.now();
      inFlight = conn.fetchWorld();
      try {
        return await inFlight;
      } catch (e) {
        // A failed fetch leaves the world stale so the next frame tries again,
        // rather than leaving the client quietly predicting a world it knows
        // it has lost.
        stale = true;
        hooks.onNotice?.(`could not refetch the world: ${(e as Error).message}`);
        return null;
      } finally {
        inFlight = null;
      }
    },

    look(p) { conn.send('presence', p); },

    async act(r) {
      const ack = await conn.request('act', {
        verb: r.verb, x: r.x, z: r.z, radius: r.focus.radius,
        ...(r.from ? { from: r.from } : {}),
        ...(r.spend !== undefined ? { spend: r.spend } : {}),
        ...(r.target ? { target: r.target } : {}),
      }).catch(() => null);
      if (!ack) return null;
      refused(ack);
      return resultOf(ack, 'act')?.act ?? null;
    },

    async grasp(r) {
      const ack = await conn.request('grasp', r).catch(() => null);
      if (!ack) return null;
      refused(ack);
      return resultOf(ack, 'grasp')?.grasp ?? null;
    },

    async anchor(r) {
      const ack = await conn.request('anchor', r).catch(() => null);
      if (!ack) return null;
      refused(ack);
      return resultOf(ack, 'anchor')?.anchor ?? null;
    },

    close() { conn.close(); },
  };

  return { world, snapshot };
}
